/** Grid key sorting. Client-side: the grid already ships the whole key set. */
import type { GridKey } from '../api/types'
import { cellStatus } from './status'

export type SortKey =
  | 'added-desc'
  | 'added-asc'
  | 'updated-desc'
  | 'name-asc'
  | 'name-desc'
  | 'untranslated-first'

export const SORT_LABELS: Record<SortKey, string> = {
  'added-desc': 'Last added',
  'added-asc': 'Oldest first',
  'updated-desc': 'Recently updated',
  'name-asc': 'Key A–Z',
  'name-desc': 'Key Z–A',
  'untranslated-first': 'Least translated',
}

export const DEFAULT_SORT: SortKey = 'added-desc'

/** Untranslated cells across the locales currently in view. */
function missingCount(key: GridKey, codes: string[]): number {
  let missing = 0
  for (const code of codes) {
    for (const cell of key.cells[code] ?? []) {
      if (cellStatus(cell) === 'untranslated') missing += 1
    }
  }
  return missing
}

/**
 * Sorted copy of `keys`. Every comparator falls back to name so the order is
 * total — otherwise rows with equal timestamps (a bulk import stamps thousands
 * in the same second) shuffle between renders and the grid looks broken.
 */
export function sortKeys(keys: GridKey[], sort: SortKey, visibleCodes: string[]): GridKey[] {
  const byName = (a: GridKey, b: GridKey) => a.name.localeCompare(b.name)
  const comparators: Record<SortKey, (a: GridKey, b: GridKey) => number> = {
    'added-desc': (a, b) => b.created_at.localeCompare(a.created_at) || byName(a, b),
    'added-asc': (a, b) => a.created_at.localeCompare(b.created_at) || byName(a, b),
    'updated-desc': (a, b) => b.updated_at.localeCompare(a.updated_at) || byName(a, b),
    'name-asc': byName,
    'name-desc': (a, b) => byName(b, a),
    'untranslated-first': (a, b) =>
      missingCount(b, visibleCodes) - missingCount(a, visibleCodes) || byName(a, b),
  }
  return [...keys].sort(comparators[sort] ?? byName)
}
